import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { API_URLS, ROLE_LABELS } from "../config";
import { MdOutlineShield } from "react-icons/md";
import { IoSettingsSharp } from "react-icons/io5";
import { FaUserGroup } from "react-icons/fa6";
import { GoHomeFill } from "react-icons/go";
import { FaRegFolderOpen, FaUserTie } from "react-icons/fa6";
import { IoIosSearch } from "react-icons/io";
import { FiBook, FiLayers, FiBox, FiActivity, FiAward, FiDollarSign } from "react-icons/fi";
import { TbBuildingStore } from "react-icons/tb";
import "./AccessPanel.css";

// ─── Role colours ────────────────────────────────────────────────────────────
const ROLE_COLORS = {
  global_superadmin: { bg: "#7c3aed", light: "#ede9fe" },
  company_admin:     { bg: "#0891b2", light: "#f9fafb" },
  admin:             { bg: "#2563eb", light: "#eff6ff" },
  hod:               { bg: "#059669", light: "#ecfdf5" },
  project_manager:   { bg: "#d97706", light: "#fffbeb" },
  coordinator:       { bg: "#6b7280", light: "#f9fafb" },
  it:                { bg: "#0891b2", light: "#ecfeff" },
  finance:           { bg: "#16a34a", light: "#f0fdf4" },
};

// ─── Method badge colours ────────────────────────────────────────────────────
const METHOD_COLORS = {
  GET:    { bg: "#dcfce7", color: "#166534" },
  POST:   { bg: "#dbeafe", color: "#1e40af" },
  PUT:    { bg: "#fef9c3", color: "#854d0e" },
  DELETE: { bg: "#fee2e2", color: "#991b1b" },
};

// ─── Page → API auto-link map ─────────────────────────────────────────────────
// When a page is toggled ON, these API keys are also toggled ON automatically.
const PAGE_API_LINKS = {
  dashboard:                ["dashboard_data"],
  company_management:       ["company_list","company_get","company_create","company_edit","company_toggle"],
  user_management:          ["users_list","users_managers"],
  user_form:                ["users_create","users_managers"],
  view_user:                ["users_view"],
  edit_user:                ["users_edit","users_view","users_managers"],
  access_panel:             ["access_config","access_list","access_get","access_update"],
  master_vendors:           ["vendor_list","vendor_get","vendor_create","vendor_edit","vendor_delete"],
  master_customers:         ["customer_list","customer_get","customer_create","customer_edit","customer_delete"],
  master_discipline:        ["discipline_list","discipline_get","discipline_create","discipline_edit","discipline_delete"],
  master_subdiscipline:     ["subdiscipline_list","subdiscipline_get","subdiscipline_create","subdiscipline_edit","subdiscipline_delete"],
  master_product:           ["product_list","product_get","product_create","product_edit","product_delete"],
  master_service:           ["service_list","service_get","service_create","service_edit","service_delete"],
  master_certificate:       ["certificate_list","certificate_get","certificate_create","certificate_edit","certificate_delete"],
  master_inspectors:        ["inspector_list","inspector_get","inspector_create","inspector_edit","inspector_delete"],
  enquiry_register:         [
    // Core enquiry CRUD
    "enquiry_list","enquiry_get","enquiry_create","enquiry_edit","enquiry_delete",
    // Enquiry list page lookups (Enquiry.jsx)
    "customer_list","customer_create", "customer_get" ,
    "vendor_create" ,"vendor_get","vendor_list",
    "users_list","users_view",
    "inspector_list","inspector_get","inspector_create",
    "product_list","product_create",
    "discipline_list","discipline_get" ,
    "subdiscipline_get","subdiscipline_list",
    "product_get",
    "service_list","service_get",
    "certificate_list",
    "vendor_create",
  ],
  };

// ─── Page groups (tree) ──────────────────────────────────────────────────────
const PAGE_GROUPS = [
  { label:"Dashboard & Company", icon:<GoHomeFill />, keys:["dashboard","company_management"] },
  { label:"User Management",     icon:<FaUserGroup />, keys:["user_management","user_form","view_user","edit_user"] },
  { label:"Administration",      icon:<IoSettingsSharp />,  keys:["access_panel", "past_records"] },
  { label:"Master Data",         icon:<FaRegFolderOpen />, keys:["master_vendors", "master_customers", "master_discipline", "master_subdiscipline", "master_product", "master_service", "master_certificate", "master_inspectors"] },
  { label:"Enquiry",             icon:<IoIosSearch />, keys:["enquiry_register"] },
  { label:"Finance",             icon:<FiDollarSign />, keys:["finance_dashboard","finance_customer_invoice","finance_inspection_invoice","finance_currency_rates"] },
];

// ─── API groups (tree) ───────────────────────────────────────────────────────
const API_GROUPS = [
  { label:"Authentication",  icon:"🔐", keys:["auth_login","auth_refresh","auth_logout","auth_me","auth_change_password"] },
  { label:"Users",           icon:"👤", keys:["users_list","users_create","users_view","users_edit","users_delete","users_toggle","users_stats","users_managers"] },
  { label:"Companies",       icon:"🏢", keys:["company_list","company_get","company_create","company_edit","company_toggle","company_delete"] },
  { label:"Access Control",  icon:"🛡️",  keys:["access_config","access_list","access_get","access_update"] },
  { label:"Dashboard",       icon:"📊", keys:["dashboard_data"] },
  { label:"Vendor Master",    icon:<TbBuildingStore />, keys:["vendor_list","vendor_get","vendor_create","vendor_edit","vendor_delete"] },
  { label:"Customer Master",  icon:<FaUserTie />,    keys:["customer_list","customer_get","customer_create","customer_edit","customer_delete"] },
  { label:"Discipline Master", icon:<FiBook />,        keys:["discipline_list","discipline_get","discipline_create","discipline_edit","discipline_delete"] },
  { label:"Sub-Discipline Master", icon:<FiLayers />, keys:["subdiscipline_list","subdiscipline_get","subdiscipline_create","subdiscipline_edit","subdiscipline_delete"] },
  { label:"Product Master",   icon:<FiBox />, keys:["product_list","product_get","product_create","product_edit","product_delete"] },
  { label:"Service Master",   icon:<FiActivity />, keys:["service_list","service_get","service_create","service_edit","service_delete"] },
  { label:"Certificate Master", icon:<FiAward />,   keys:["certificate_list","certificate_get","certificate_create","certificate_edit","certificate_delete"] },
  { label:"Inspector Master", icon:<FaUserGroup />, keys:["inspector_list","inspector_get","inspector_create","inspector_edit","inspector_delete"] },
  { label:"Enquiry",          icon:<IoIosSearch />, keys:["enquiry_list","enquiry_get","enquiry_create","enquiry_edit","enquiry_delete","enquiry_export"] },
  { label:"Finance",          icon:<FiDollarSign />, keys:["finance_dashboard","finance_sales_invoice_list","finance_sales_invoice_create","finance_sales_invoice_view","finance_sales_invoice_edit","finance_sales_invoice_delete","finance_sales_invoice_confirm","finance_currency_rates_edit"] },
  ];

const ITEMS_PER_PAGE = 10;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const initials = (name = "") =>
  name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

// ─── TreeGroup component ──────────────────────────────────────────────────────
const TreeGroup = ({ group, items, checked, onToggleAll, onToggleItem }) => {
  const [open, setOpen] = useState(true);
  const groupItems = items.filter(it => group.keys.includes(it.key));
  if (!groupItems.length) return null;

  const checkedCount = groupItems.filter(it => checked.includes(it.key)).length;
  const allChecked  = checkedCount === groupItems.length;
  const someChecked = checkedCount > 0 && !allChecked;

  return (
    <div className="ap-tree-group">
      <div className="ap-tree-group-header" onClick={() => setOpen(o => !o)}>
        <input
          type="checkbox"
          className="ap-checkbox"
          checked={allChecked}
          ref={el => { if (el) el.indeterminate = someChecked; }}
          onChange={e => { e.stopPropagation(); onToggleAll(groupItems.map(i => i.key), !allChecked); }}
          onClick={e => e.stopPropagation()}
        />
        <span className="ap-tree-group-icon">{group.icon}</span>
        <span className="ap-tree-group-label">{group.label}</span>
        <span className="ap-tree-group-count">{checkedCount}/{groupItems.length}</span>
        <span className="ap-tree-chevron">{open ? "▾" : "▸"}</span>
      </div>

      {open && (
        <div className="ap-tree-items">
          {groupItems.map(item => {
            const isChecked = checked.includes(item.key);
            const mc = item.method ? (METHOD_COLORS[item.method] || {}) : null;
            return (
              <div
                key={item.key}
                className={`ap-tree-item${isChecked ? " checked" : ""}`}
                onClick={() => onToggleItem(item.key)}
              >
                <input
                  type="checkbox"
                  className="ap-checkbox"
                  checked={isChecked}
                  onChange={() => onToggleItem(item.key)}
                  onClick={e => e.stopPropagation()}
                />
                {mc && (
                  <span className="ap-method-badge" style={{ background: mc.bg, color: mc.color }}>
                    {item.method}
                  </span>
                )}
                <div className="ap-tree-item-label">
                  <span className="ap-tree-item-name">{item.label}</span>
                  <span className="ap-tree-item-path">{item.path}</span>
                </div>
                {isChecked && <span className="ap-check-tick">✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── TreeSection component ────────────────────────────────────────────────────
const TreeSection = ({ title, icon, count, total, onSelectAll, allSelected, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="ap-tree-section">
      <div className="ap-tree-section-header" onClick={() => setOpen(o => !o)}>
        <span>{icon}</span>
        <span className="ap-tree-section-title">{title}</span>
        <span className="ap-tree-section-count">{count}/{total}</span>
        <button
          className="ap-select-all-btn"
          onClick={e => { e.stopPropagation(); onSelectAll(); }}
        >
          {allSelected ? "Deselect All" : "Select All"}
        </button>
        <span className="ap-tree-chevron">{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="ap-tree-section-body">{children}</div>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const AccessPanel = () => {
  const { token, user: currentUser, refreshAccess } = useAuth();

  const [config, setConfig]           = useState({ pages: [], apis: [] });
  const [users, setUsers]             = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [access, setAccess]           = useState({ pages: [], apis: [] });
  const [loadingList, setLoadingList] = useState(true);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState("");
  const [page, setPage]               = useState(1);
  const [toast, setToast]             = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Load config
  useEffect(() => {
    fetch(`${API_URLS.ACCESS}/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setConfig({ pages: data.pages || [], apis: data.apis || [] }); })
      .catch(console.error);
  }, [token]);

  // Load user list
  const fetchUsers = useCallback(async () => {
    setLoadingList(true);
    try {
      const res  = await fetch(API_URLS.ACCESS, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.ok ? await res.json() : [];
      setUsers((Array.isArray(data) ? data : []).map(u => ({ ...u, id: String(u.id || u._id) })));
    } catch { setUsers([]); }
    finally { setLoadingList(false); }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Select user → load their access
  const openUser = async (u) => {
    setSelectedUser(u);
    setLoadingAccess(true);
    setAccess({ pages: [], apis: [] });
    try {
      const res  = await fetch(`${API_URLS.ACCESS}/${u.id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.ok ? await res.json() : {};
      setAccess({ pages: Array.isArray(data.pages) ? data.pages : [], apis: Array.isArray(data.apis) ? data.apis : [] });
    } catch { showToast("Failed to load user access", "error"); }
    finally { setLoadingAccess(false); }
  };

  // ── Toggle helpers with page→API auto-link ────────────────────────────────
  const applyPageToggle = (currentAccess, pageKey, shouldAdd) => {
    const linked = PAGE_API_LINKS[pageKey] || [];
    let newPages = currentAccess.pages;
    let newApis  = currentAccess.apis;

    if (shouldAdd) {
      newPages = [...new Set([...newPages, pageKey])];
      // Auto-add linked APIs
      newApis  = [...new Set([...newApis, ...linked])];
    } else {
      newPages = newPages.filter(k => k !== pageKey);
      // Auto-remove linked APIs only if no other page still needs them
      const stillNeeded = new Set(
        newPages.flatMap(p => PAGE_API_LINKS[p] || [])
      );
      newApis = newApis.filter(k => !linked.includes(k) || stillNeeded.has(k));
    }
    return { pages: newPages, apis: newApis };
  };

  const togglePageItem = (key) => {
    setAccess(prev => {
      const shouldAdd = !prev.pages.includes(key);
      return applyPageToggle(prev, key, shouldAdd);
    });
  };

  const togglePageGroup = (keys, shouldAdd) => {
    setAccess(prev => {
      let next = { ...prev };
      keys.forEach(k => { next = applyPageToggle(next, k, shouldAdd); });
      return next;
    });
  };

  const toggleApiItem = (key) => {
    setAccess(prev => {
      const arr = prev.apis;
      return { ...prev, apis: arr.includes(key) ? arr.filter(k => k !== key) : [...arr, key] };
    });
  };

  const toggleApiGroup = (keys, shouldAdd) => {
    setAccess(prev => {
      if (shouldAdd) return { ...prev, apis: [...new Set([...prev.apis, ...keys])] };
      return { ...prev, apis: prev.apis.filter(k => !keys.includes(k)) };
    });
  };

  const toggleAllPages = () => {
    const allKeys = config.pages.map(p => p.key);
    const allOn   = allKeys.every(k => access.pages.includes(k));
    setAccess(prev => {
      if (allOn) return { ...prev, pages: [], apis: [] };
      let next = { ...prev };
      allKeys.forEach(k => { next = applyPageToggle(next, k, true); });
      return next;
    });
  };

  const toggleAllApis = () => {
    const allKeys    = config.apis.map(a => a.key);
    const allChecked = allKeys.every(k => access.apis.includes(k));
    setAccess(prev => ({ ...prev, apis: allChecked ? [] : allKeys }));
  };

  // Save
  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const res  = await fetch(`${API_URLS.ACCESS}/${selectedUser.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pages: access.pages, apis: access.apis }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(`✓ Access saved for ${selectedUser.fullName}`);
        if (String(selectedUser.id) === String(currentUser?._id || currentUser?.id)) refreshAccess();
        setUsers(prev => prev.map(u =>
          u.id === selectedUser.id
            ? { ...u, pagesCount: access.pages.length, apisCount: access.apis.length }
            : u
        ));
      } else {
        showToast(data.message || "Failed to save", "error");
      }
    } catch { showToast("Network error", "error"); }
    finally { setSaving(false); }
  };

  // ── Pagination ────────────────────────────────────────────────────────────
  const filteredUsers = useMemo(() =>
    users.filter(u =>
      !search ||
      u.fullName?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.employeeCode?.toLowerCase().includes(search.toLowerCase())
    ),
    [users, search]
  );

  const totalPages   = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  const pagedUsers   = filteredUsers.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const handleSearch = (v) => { setSearch(v); setPage(1); };

  const allPagesChecked = config.pages.length > 0 && config.pages.every(p => access.pages.includes(p.key));
  const allApisChecked  = config.apis.length  > 0 && config.apis.every(a => access.apis.includes(a.key));

  return (
    <div className="ap-page">

      {/* Toast */}
      {toast && (
        <div className={`ap-toast${toast.type === "error" ? " error" : ""}`}>
          {toast.msg}
        </div>
      )}

      <div className="ap-layout">

        {/* ── LEFT: User List ────────────────────────────────────────────── */}
        <div className="ap-user-panel">
          <div className="ap-user-panel-header">
            <span className="ap-user-panel-title"><MdOutlineShield /> Access Panel</span>
            <span className="ap-user-panel-count">{filteredUsers.length} users</span>
          </div>

          <div className="ap-search-box">
            <span className="ap-search-icon"><IoIosSearch /></span>
            <input
              placeholder="Search name"
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>

          <div className="ap-user-list">
            {loadingList ? (
              <div className="ap-list-placeholder">Loading users…</div>
            ) : pagedUsers.length === 0 ? (
              <div className="ap-list-placeholder">No users found</div>
            ) : pagedUsers.map(u => {
              const rc = ROLE_COLORS[u.role] || { bg: "#6b7280", light: "#f9fafb" };
              const isSelected = selectedUser?.id === u.id;
              return (
                <div
                  key={u.id}
                  className={`ap-user-item${isSelected ? " selected" : ""}`}
                  onClick={() => openUser(u)}
                >
                  <div
                    className="ap-avatar"
                    style={{ background: rc.bg, color: "#fff" }}
                  >
                    {initials(u.fullName)}
                  </div>
                  <div className="ap-user-info">
                    <div className="ap-user-name">
                      {u.fullName}
                      {u.isCompanyAdmin && <span className="ap-ca-badge">CA</span>}
                    </div>
                    <div className="ap-user-sub">
                      <span
                        className="ap-role-badge"
                        style={{ background: rc.light, color: rc.bg }}
                      >
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                      <span className="ap-emp-code">{u.employeeCode}</span>
                    </div>
                    <div className="ap-user-counts">
                      <span>📄 {u.pagesCount ?? 0} pages</span>
                      <span>🔌 {u.apisCount ?? 0} apis</span>
                    </div>
                  </div>
                  <span className="ap-arrow">›</span>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ap-pagination">
              <button
                className="ap-page-btn"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >‹</button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  className={`ap-page-btn${p === page ? " active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}

              <button
                className="ap-page-btn"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >›</button>
            </div>
          )}
        </div>

        {/* ── RIGHT: Editor ──────────────────────────────────────────────── */}
        <div className="ap-editor">

          {!selectedUser ? (
            <div className="ap-editor-empty">
              <div className="ap-editor-empty-icon">🔐</div>
              <div className="ap-editor-empty-title">No user selected</div>
              <div className="ap-editor-empty-sub">Select a user from the left to manage their access rights</div>
            </div>

          ) : loadingAccess ? (
            <div className="ap-editor-empty">
              <div className="ap-editor-empty-sub">Loading access…</div>
            </div>

          ) : (
            <>
              {/* Editor header */}
              <div className="ap-editor-header">
                {(() => {
                  const rc = ROLE_COLORS[selectedUser.role] || { bg: "#6b7280", light: "#f9fafb" };
                  return (
                    <>
                      <div className="ap-avatar ap-avatar-lg" style={{ background: rc.bg, color: "#fff" }}>
                        {initials(selectedUser.fullName)}
                      </div>
                      <div className="ap-editor-user-info">
                        <div className="ap-editor-user-name">{selectedUser.fullName}</div>
                        <div className="ap-editor-user-sub">
                          <span>{selectedUser.employeeCode}</span>
                          <span>·</span>
                          <span>{selectedUser.email}</span>
                        </div>
                        <span className="ap-role-badge" style={{ background: rc.light, color: rc.bg }}>
                          {ROLE_LABELS[selectedUser.role] || selectedUser.role}
                        </span>
                      </div>
                      <button
                        className="ap-save-btn"
                        onClick={save}
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "✓ Save Access"}
                      </button>
                    </>
                  );
                })()}
              </div>

              {/* Counter bar */}
              <div className="ap-counter-bar">
                <div className="ap-counter">
                  <span className="ap-counter-label">📄 Pages</span>
                  <span className="ap-counter-val">{access.pages.length}</span>
                  <span className="ap-counter-total">/ {config.pages.length}</span>
                </div>
                <div className="ap-counter">
                  <span className="ap-counter-label">🔌 APIs</span>
                  <span className="ap-counter-val" style={{ color: "#0078d4" }}>{access.apis.length}</span>
                  <span className="ap-counter-total">/ {config.apis.length}</span>
                </div>
                {access.pages.length === 0 && (
                  <span className="ap-warn">⚠ No pages — user will be blocked</span>
                )}
                <div className="ap-auto-link-note">
                  💡 Page access auto-links related APIs
                </div>
              </div>

              {/* Tree body */}
              <div className="ap-editor-body">

                {/* Pages tree */}
                <TreeSection
                  title="Pages"
                  icon="📄"
                  count={access.pages.length}
                  total={config.pages.length}
                  onSelectAll={toggleAllPages}
                  allSelected={allPagesChecked}
                >
                  {PAGE_GROUPS.map(group => (
                    <TreeGroup
                      key={group.label}
                      group={group}
                      items={config.pages}
                      checked={access.pages}
                      onToggleAll={(keys, val) => togglePageGroup(keys, val)}
                      onToggleItem={togglePageItem}
                    />
                  ))}
                </TreeSection>

                {/* APIs tree */}
                <TreeSection
                  title="Permissions"
                  icon="🔌"
                  count={access.apis.length}
                  total={config.apis.length}
                  onSelectAll={toggleAllApis}
                  allSelected={allApisChecked}
                >
                  {API_GROUPS.map(group => (
                    <TreeGroup
                      key={group.label}
                      group={group}
                      items={config.apis}
                      checked={access.apis}
                      onToggleAll={(keys, val) => toggleApiGroup(keys, val)}
                      onToggleItem={toggleApiItem}
                    />
                  ))}
                </TreeSection>

              </div>

              {/* Footer save */}
              <div className="ap-editor-footer">
                <button className="cancel-btn" onClick={() => { setSelectedUser(null); setAccess({ pages: [], apis: [] }); }}>
                  Close
                </button>
                <button
                  className="save-btn"
                  style={{ background: saving ? "#ccc" : "#d53e40" }}
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "✓ Save Access"}
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default AccessPanel;
